import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Injector,
    Input,
    InputSignal,
    OnDestroy,
    OnInit,
    Output,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    output
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    MatAutocomplete,
    MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatChipGrid, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { IDatiqGeoSuggestion } from '@dat/iq-proxy';
import { IqControlTextStateService } from '../state';
import { ElementRef, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { IqEventBusBrokerHandler, IqEventBusBrokerService } from '@dat/iq-event-bus';
import { GeoControlEventTypes } from '../models';

@Component({
    selector: 'iq-control-text-geo',
    standalone: true,
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatChipsModule,
        MatIconModule,
        AsyncPipe,
        ReactiveFormsModule,
        MatAutocompleteModule,
    ],
    templateUrl: './iq-control-text-geo.component.html',
    styleUrl: './iq-control-text-geo.component.scss',
})
export class IqControlTextGeoComponent implements OnInit, OnDestroy {
    randomName: string =
        'iq-control-text-percent-' + Math.random().toString(36).substring(7);

    @Input()
    fg: FormGroup = new FormGroup({});

    changed = output<IDatiqGeoSuggestion>();

    searching = output<boolean>();

    loaded = output<any>();

    focused = output();

    inputControl: FormControl;

    errorMessageSignal: Signal<string> = signal('');
    errorMessage: Signal<string> = computed((): string => {
        return this.errorMessageSignal();
    });
    queryCtrl: FormControl;
    @ViewChild('chipGrid') chipGrid: MatChipGrid;

    // Call this in the OnInit Function
    private _setErrorMessageSignal(): void {
        this.errorMessageSignal = this._state.errorMessage(
            this.fg.get(this.name()) as FormControl,
        );
    }

    getErrorMessage(): string {
        return this._state.getErrorMessage(this.inputControl);
    }

    geoSuggestions: WritableSignal<IDatiqGeoSuggestion[]> = signal([]);
    query: Signal<string> = signal('');

    currentSuggestions: WritableSignal<IDatiqGeoSuggestion[]> = signal([]);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    id: InputSignal<string> = input('');
    customErrorMessage: InputSignal<string> = input('');
    locationErrorMessage: InputSignal<string> = input('');
    label: InputSignal<string> = input('');
    placeholder: InputSignal<string> = input('');
    value: InputSignal<string> = input('');
    name: InputSignal<string> = input('');
    disabled: InputSignal<boolean> = input(null);
    required: InputSignal<boolean> = input(null);
    startFocused: InputSignal<boolean> = input(null);

    suggestions: IDatiqGeoSuggestion[] = [];

    hasSuggestion: Signal<boolean> = computed(
        () => this.currentSuggestions().length > 0,
    );

    searchPending: boolean = false;

    inputValue: Signal<string> = signal('');

    @ViewChild('queryEle') queryEle: ElementRef<HTMLInputElement>;

    @ViewChild(MatAutocomplete) autocomplete: MatAutocomplete;

    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};

    inputControlError: Signal<any> = signal(null);

    constructor(
        private readonly _state: IqControlTextStateService,
        private readonly _fb: FormBuilder,
        private readonly _injector: Injector,
    ) {
        effect(async () => {
            const value: string = this.value();

            if (!value?.length) {
                untracked(() => {
                    this.resetControlValues();
                });
                return null;
            };

            untracked(async () => {
                const currentSuggestions =
                    await this._state.fetchGeoSuggestions(this.value());

                this.suggestions = [currentSuggestions[0]];
                this.inputControl.setValue(currentSuggestions[0].displayName);
            });
        });
        const initializeEffect = effect(async () => {
            const currentSuggestions = await this._state.fetchGeoSuggestions(this.value());
            if (currentSuggestions[0] && currentSuggestions[0].displayName?.length > 0) {
                this.loaded.emit(currentSuggestions[0]?.displayName);
                initializeEffect.destroy();
            }
        });
        effect(() => {
            const query = this.query();
            
            if (!query?.length) return;

            if (query?.length < 3) {
                this.searchPending = true;
                return;
            }

            untracked(async () => {
                const suggestions =
                    await this._state.fetchGeoSuggestions(query);
                this.geoSuggestions.set(suggestions);
                this.searchPending = false;
            });
        });

        effect(() => {
            this.inputControlError();
            this.updateFormError();
        });

        this._deregisterMap = this._bus.registerMap({
            onLocationError: {
                callback: (payload: any) => {
                    if (payload.id === this.id()) {
                        this.inputControl.setErrors({ locationError: true });
                        this.chipGrid.errorState = true;
                    }
                }
            }
        });
    }

    async ngOnInit(): Promise<void> {
        this.queryCtrl = this._fb.control('');
        this.query = toSignal(this.queryCtrl?.valueChanges, {
            injector: this._injector,
        });

        this.initializeInputs();
        await this._setInitialSuggestions();
    }

    private initializeInputs() {
        if (!this.name()?.length) return;

        this.fg.addControl(
            this.name(),
            this._fb.control(null, [this._state.geoRequired(this.required())]),
        );

        this.inputControl = this.fg.get(this.name()) as FormControl;
        this.inputValue = toSignal(this.inputControl?.valueChanges, {
            injector: this._injector,
        });

        this.inputControlError = toSignal(
            this.inputControl?.statusChanges,
            { injector: this._injector },
        );
        this._setErrorMessageSignal();
    }

    async _setInitialSuggestions(): Promise<void> {
        if (!this.value()?.length && !this.name()?.length) return;

        const initialSuggestions = await this._state.fetchGeoSuggestions(
            this.value(),
        );

        this.inputControl.setValue(initialSuggestions[0]?.displayName);
    }

    remove(): void {
        this.queryCtrl.markAsTouched();
        this.resetControlValues();
        this.changed.emit(null);
        this._dispatchEvent(GeoControlEventTypes.Deselect);
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        if (event.option?.value) {
            this.currentSuggestions.set([
                event.option.value as IDatiqGeoSuggestion,
            ]);
            this.suggestions = [event.option.value];
            this.queryEle.nativeElement.value = '';
            this.queryCtrl.setValue(null);
            this.inputControl.setValue(event.option.value.displayName);
            this.geoSuggestions.set([]);
            this.autocomplete._keyManager.setActiveItem(null);
        }
        this.updateFormError();
        this.changed.emit(event.option.value);
        this._dispatchEvent(GeoControlEventTypes.Select);
    }

    onTab(): void {
        this.updateFormError();
        if (
            this.autocomplete._keyManager.activeItem &&
            this.autocomplete._keyManager.activeItem.value
        ) {
            this.selected(
                new MatAutocompleteSelectedEvent(
                    this.autocomplete,
                    this.autocomplete._keyManager.activeItem,
                ),
            );
        }
    }

    submitted(): void {
        if (!this.fg.valid) return;

        /**
         * This dispatches an event indicating the form is submitted.
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onSubmit": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "control.id",
         *                "operator": "eq",
         *                "value": "CONTROL_ID"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
        this._dispatchEvent(GeoControlEventTypes.Submit);
    }

    updateFormError(): void {
        if(!this.required()) {
            this.chipGrid.errorState = false;
            return;
        }
        if (this.inputControl.invalid && this.queryCtrl.touched) {
            this.chipGrid.errorState = true;
        } else {
            this.chipGrid.errorState = false;
        }
    }

    private createEmitData() {
        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
        };
        return emitData;
    }

    private _dispatchEvent(eventName: GeoControlEventTypes): void {
        this._bus.dispatch({
            name: `type:${eventName}`,
            payload: {
                id: this.id(),
                ...this.createEmitData(),
            },
        });
    }


    resetControlValues(): void {
        this.geoSuggestions.set([]);
        this.suggestions = [];
        this.inputControl.setValue(null);
        this.updateFormError();
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregisterMap);
    }
}
